const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8500';

type MarkdownNode = {
  type: string;
  value?: string;
  url?: string;
  alt?: string | null;
  children?: MarkdownNode[];
  data?: {
    hName?: string;
    hProperties?: Record<string, unknown>;
    hChildren?: Array<{
      type: 'element';
      tagName: string;
      properties: Record<string, unknown>;
      children: never[];
    }>;
  };
};

export const replaceAssetRoot = (content: string) => {
  return content.replace(/\$TMN_ASSET_ROOT\$/g, `${API_BASE_URL}/assets`);
};

export const normalizeMathBlockDelimiters = (content: string) => {
  return replaceAssetRoot(content)
    .replace(/\$\$([^\n])/g, '$$$\n$1')
    .replace(/([^\n])\$\$/g, '$1\n$$$')
    .replace(/\\\[/g, '$$$\n')
    .replace(/\\\]/g, '\n$$$');
};

const normalizeYouTubeHost = (hostname: string) => {
  return hostname.toLowerCase().replace(/^(www\.|m\.)/, '');
};

const parseYouTubeStart = (value: string | null) => {
  if (!value) return null;
  if (/^\d+$/.test(value)) return Number(value);

  const match = value.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/);
  if (!match || !match.slice(1).some(Boolean)) return null;

  const [, hours = '0', minutes = '0', seconds = '0'] = match;
  return Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds);
};

const getYouTubeVideoId = (url: URL) => {
  const host = normalizeYouTubeHost(url.hostname);
  const pathParts = url.pathname.split('/').filter(Boolean);

  if (host === 'youtu.be') return pathParts[0];
  if (host !== 'youtube.com' && host !== 'youtube-nocookie.com') return null;

  if (url.pathname === '/watch') return url.searchParams.get('v');
  if (['embed', 'shorts', 'live'].includes(pathParts[0])) return pathParts[1];

  return null;
};

const getYouTubeEmbedUrl = (rawUrl: string) => {
  try {
    const url = new URL(rawUrl);
    const videoId = getYouTubeVideoId(url);

    if (!videoId || !/^[a-zA-Z0-9_-]{11}$/.test(videoId)) return null;

    const embedUrl = new URL(`https://www.youtube-nocookie.com/embed/${videoId}`);
    const start = parseYouTubeStart(url.searchParams.get('start') || url.searchParams.get('t'));
    if (start && start > 0) {
      embedUrl.searchParams.set('start', String(start));
    }

    return embedUrl.toString();
  } catch {
    return null;
  }
};

const getStandaloneLinkUrl = (node: MarkdownNode) => {
  if (!node.children) return null;

  const contentNodes = node.children.filter((child) => {
    return child.type !== 'text' || Boolean(child.value?.trim());
  });

  if (contentNodes.length !== 1) return null;

  const [child] = contentNodes;
  if (child.type === 'link' && child.url) return child.url;
  if (child.type === 'text' && child.value) return child.value.trim();

  return null;
};

const applyYouTubeEmbed = (node: MarkdownNode, embedUrl: string) => {
  node.data = {
    hName: 'div',
    hProperties: {
      className: 'markdown-youtube-embed',
    },
    hChildren: [
      {
        type: 'element',
        tagName: 'iframe',
        properties: {
          src: embedUrl,
          title: 'YouTube video player',
          loading: 'lazy',
          allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
          allowFullScreen: true,
          referrerPolicy: 'strict-origin-when-cross-origin',
        },
        children: [],
      },
    ],
  };
};

export const remarkYouTubeEmbeds = () => {
  const transform = (node: MarkdownNode) => {
    if (!node.children) return;

    node.children.forEach((child) => {
      if (child.type === 'paragraph') {
        const linkUrl = getStandaloneLinkUrl(child);
        const embedUrl = linkUrl ? getYouTubeEmbedUrl(linkUrl) : null;

        if (embedUrl) {
          applyYouTubeEmbed(child, embedUrl);
          return;
        }
      }

      transform(child);
    });
  };

  return transform;
};

// Keep in sync with AUDIO_ASSET_EXTENSIONS in cli/main.py, which keeps the file
// name in audio asset URLs so that they can be recognised here.
const AUDIO_EXTENSION_PATTERN = /\.(mp3|m4a|aac|wav|ogg|oga|opus|flac|weba)$/i;

export const isAudioUrl = (url: string) => {
  return AUDIO_EXTENSION_PATTERN.test(url.split(/[?#]/)[0]);
};

// Renders image syntax that points at an audio file, e.g.
// `![Pronunciation](assets/hello.mp3)`, as a native audio player.
export const remarkAudioEmbeds = () => {
  const transform = (node: MarkdownNode) => {
    node.children?.forEach((child, index) => {
      if (child.type === 'image' && child.url && isAudioUrl(child.url)) {
        const label = child.alt?.trim();
        node.children![index] = {
          type: 'audioEmbed',
          data: {
            hName: 'audio',
            hProperties: {
              className: 'markdown-audio',
              controls: true,
              preload: 'metadata',
              src: child.url,
              ...(label ? { title: label, ariaLabel: label } : { ariaLabel: 'Audio' }),
            },
            hChildren: [],
          },
        };
        return;
      }
      transform(child);
    });
  };

  return transform;
};
